import React, { useEffect, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { t } from "ttag";

import { HeroSlideBlock, HeroCarouselBlock as Value } from "@reactivated";

import {
    HeroLayoutProps,
    HeroLayoutSettings,
} from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";

import { Themed } from "../../styles/themes";
import { Carousel } from "../Carousel/Carousel";
import { Clickable } from "../Clickables";
import { MediaChooserBlock } from "../MediaChooserBlock";
import {
    RichTextStreamBlock,
    containerWidthToColumns,
} from "../RichTextStreamBlock";
import { SKSDots } from "../SKSCarousel/Dots";
import { MotionPauseContext } from "../SKSCarousel/MotionPauseContext";
import {
    isAutoplayVideoMedia,
    useVideoPacedAutoPlay,
} from "../SKSCarousel/useVideoPacedAutoPlay";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

function AutoPlayControl({ isPaused }: { isPaused: boolean }) {
    return (
        <Clickable slot="autoplay" className={styles.pauseButton}>
            <VisuallyHidden>{isPaused ? t`Play` : t`Pause`}</VisuallyHidden>
            <Svg name={isPaused ? "play" : "pause"} aria-hidden="true" />
        </Clickable>
    );
}

interface SlideProps {
    slide: HeroSlideBlock;
    height: string;
    hasDots: boolean;
    hideBodyOnMobile: boolean;
    videoProps?: React.ComponentProps<typeof MediaChooserBlock>["videoProps"];
}

const Slide = ({
    slide,
    height,
    hasDots,
    hideBodyOnMobile,
    videoProps,
}: SlideProps) => {
    if (!slide.media) return null;

    const media = (
        <MediaChooserBlock value={slide.media} videoProps={videoProps} />
    );
    // videoProps goes to both: only video media reads it, and only the
    // breakpoint-visible element ever loads and plays
    const mobileMedia =
        slide.mobile_media && slide.mobile_media.length > 0 ? (
            <MediaChooserBlock
                value={slide.mobile_media}
                videoProps={videoProps}
            />
        ) : (
            media
        );

    const contentValue = slide.content;
    const contentBlocks = contentValue?.content ?? [];
    const hasContent = contentBlocks.length > 0;

    // Bridges CMS configuration to flexihero settings
    // RichTextStreamBlock's container_width is overridden to "none" below because
    // this bit makes it unnecessary
    const contentWidth = containerWidthToColumns(
        slide.content?.container_width,
    );

    const settings: HeroLayoutSettings = {
        heroDirection: "row",
        heroPlacement: "backgroundFull",
        mediaPlacement: "background",
        mediaAspectRatio: "stretch",
        contentAlignPrimary: "right/bottom",
        contentAlignSecondary: "left/top",
        textAlignment: "left",
        contentWidth,
        minHeight: height,
        maxHeight: height,
    };

    const content =
        contentValue && hasContent ? (
            <RichTextStreamBlock
                value={{ ...contentValue, container_width: "none" }}
            />
        ) : null;

    // Dropping body blocks from the mobile layout's content.
    // Skip when body is the slide's only content.
    const mobileBlocks = hideBodyOnMobile
        ? contentBlocks.filter((block) => block.type !== "body")
        : null;

    const mobileContent =
        contentValue && mobileBlocks && mobileBlocks.length > 0 ? (
            <RichTextStreamBlock
                value={{
                    ...contentValue,
                    container_width: "none",
                    content: mobileBlocks,
                }}
            />
        ) : (
            content
        );

    const heroStyles: HeroLayoutProps["styles"] = {
        mediaClassName: hasContent ? styles.overlay : undefined,
        contentClassName: hasDots ? styles.contentWithDots : styles.content,
    };

    return (
        <HeroSet
            layouts={{
                desktop: { content, media, settings, styles: heroStyles },
                mobile: {
                    content: mobileContent,
                    media: mobileMedia,
                    settings: {
                        ...settings,
                        contentWidth: 12,
                    },
                    styles: heroStyles,
                },
            }}
        />
    );
};

export const HeroCarouselBlock = ({ value }: { value: Value }) => {
    const [viewportHeight, setViewportHeight] = useState("100dvh");
    const [isUserPaused, setIsUserPaused] = useState(false);

    // SSR-safe fallback for browsers without dvh support
    useEffect(() => {
        if (!CSS.supports("height", "100dvh")) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- SSR-safe fallback: CSS.supports is only available after mount
            setViewportHeight("100vh");
        }
    }, []);

    const height = `calc(${viewportHeight} - var(--top-nav-height, 0px))`;

    const slides = value.slides ?? [];
    const hasMultipleSlides = slides.length > 1;
    const hideBodyOnMobile = value.hide_body_on_mobile ?? true;

    const {
        carouselApi,
        carouselContainer,
        isAutoPlayRunning,
        videoPacesAutoPlay,
        onActiveChange,
        onAutoPlayRunningChange,
        videoPropsFor,
    } = useVideoPacedAutoPlay({
        // Gate pacing on whichever breakpoint's media is the video (a slide
        // can be image-on-desktop/video-on-mobile or vice versa). Only the
        // visible breakpoint's element ever loads and plays, so the hook's
        // phase tracking stays breakpoint-correct on its own.
        slideMedia: slides.map((slide) =>
            isAutoplayVideoMedia(slide.mobile_media)
                ? slide.mobile_media
                : slide.media,
        ),
        enabled: hasMultipleSlides,
    });

    if (slides.length === 0) return null;

    return (
        <Themed tag="div" theme="black" className={styles.root}>
            {/* The videos pause with every rotation-pause condition, not just
                the button, because a video may be pacing the rotation: left
                playing while rotation is paused, it would advance the carousel
                when it ends. null when there's no unified pause control, so a
                lone video shows its own play/pause instead. */}
            <MotionPauseContext.Provider
                value={hasMultipleSlides ? !isAutoPlayRunning : null}
            >
                <Carousel
                    label={value.block_label ?? t`Hero Carousel`}
                    containerProps={{ ref: carouselContainer }}
                    apiRef={carouselApi}
                    slideContainerProps={{ className: styles.slide }}
                    controls={
                        hasMultipleSlides ? (
                            <AutoPlayControl isPaused={isUserPaused} />
                        ) : null
                    }
                    dots={({ allVisible, slideCount }) => (
                        <SKSDots
                            allVisible={allVisible}
                            slideCount={slideCount}
                        />
                    )}
                    loop={hasMultipleSlides}
                    autoPlay={hasMultipleSlides}
                    autoPlayExternallyPaced={videoPacesAutoPlay}
                    pauseAutoPlayOnHover={false}
                    onAutoPlayPausedChange={setIsUserPaused}
                    onAutoPlayRunningChange={onAutoPlayRunningChange}
                    onActiveChange={onActiveChange}
                >
                    {slides.map((slide, index) => (
                        <Slide
                            key={index}
                            slide={slide}
                            height={height}
                            hasDots={hasMultipleSlides}
                            hideBodyOnMobile={hideBodyOnMobile}
                            videoProps={videoPropsFor(index)}
                        />
                    ))}
                </Carousel>
            </MotionPauseContext.Provider>
        </Themed>
    );
};
